import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowUpRight, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import BrandMark from "./BrandMark";
import MyntraPartnerBadge from "./MyntraPartnerBadge";
import ETInnovatorBadge from "./ETInnovatorBadge";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const SOCIALS = [
  { label: "WhatsApp", href: "https://wa.me/919464645491", Icon: WhatsAppIcon, hover: "#25D366" },
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61589779771836&mibextid=wwXIfr&mibextid=wwXIfr", Icon: Facebook, hover: "#1877F2" },
  { label: "Instagram", href: "https://www.instagram.com/ecomcatalogai?igsh=N3hyd204dmhjOXEy", Icon: Instagram, hover: "#E4405F" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/ecomcatalogai/", Icon: Linkedin, hover: "#0A66C2" },
  { label: "YouTube", href: "https://youtube.com/@ecomcatalogai?si=RdCRgvyEEfKweZp7", Icon: Youtube, hover: "#FF0000" },
];

const COLS = [
  {
    title: "Product",
    links: [
      { label: "Features", to: "/features" },
      { label: "Pricing", to: "/pricing" },
      { label: "AI Auto Listing", to: "/features#auto-listing" },
      { label: "Analytics", to: "/features#analytics" },
    ],
  },
  {
    title: "Marketplaces",
    links: [
      { label: "Myntra", to: "/features#myntra" },
      { label: "Nykaa", to: "/features#nykaa" },
      { label: "Ajio", to: "/features#ajio" },
      { label: "Amazon", to: "/features#amazon" },
      { label: "Flipkart", to: "/features#flipkart" },
      { label: "Blinkit · Instamart", to: "/features#qcommerce" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", to: "/blog" },
      { label: "Help Center", to: "/help" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Refund Policy", to: "/refund" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      className="relative border-t border-white/[0.06] mt-32 pt-24 pb-12 px-5 sm:px-8"
      data-testid="mkt-footer"
    >
      <div className="max-w-7xl mx-auto">
        {/* Giant call-to-action wordmark */}
        <div className="mb-24">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">
            Let's build
          </div>
          <h2 className="font-mkt-display text-[14vw] sm:text-[10vw] lg:text-[8vw] leading-[0.95] tracking-tighter font-semibold">
            Grow your brand <br className="hidden sm:block" />
            <span className="mkt-gradient-text-vivid">with AI.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="mkt-btn-gradient"
              data-testid="footer-cta-start"
            >
              Get Started <ArrowUpRight size={14} />
            </Link>
            <Link to="/pricing#pricing" className="mkt-btn-ghost" data-testid="footer-cta-demo">
              Take Monthly Plan
            </Link>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-12 border-b border-white/[0.06]">
          <div className="col-span-2">
            <BrandMark size="md" />
            <p className="text-sm text-slate-400 mt-4 max-w-xs leading-relaxed">
              AI catalog & ecommerce data analytics for sellers on Myntra, Nykaa,
              Ajio, Amazon, Flipkart, Swiggy Instamart and Blinkit.
            </p>
            <div className="mt-6 flex flex-col gap-2 text-sm text-slate-400">
              <a
                href="mailto:info@ecomcatalog.ai"
                className="inline-flex items-center gap-2 hover:text-white transition-colors"
              >
                <Mail size={14} className="text-indigo-400" />
                info@ecomcatalog.ai
              </a>
              <a
                href="tel:+919464645491"
                className="inline-flex items-center gap-2 hover:text-white transition-colors"
              >
                <Phone size={14} className="text-indigo-400" />
                +91 94646 45491
              </a>
            </div>
            {/* Social links */}
            <div className="mt-6 flex items-center gap-2" data-testid="footer-socials">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-xl grid place-items-center bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:text-white hover:bg-white/[0.1] hover:border-white/[0.2] transition-all"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>

            {/* Registered OMS Partner of Myntra — trust badge */}
            <div className="mt-6">
              <MyntraPartnerBadge variant="footer" />
            </div>

            {/* Economic Times AI Innovator nomination */}
            <div className="mt-3">
              <ETInnovatorBadge variant="footer" />
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                {c.title}
              </div>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-sm text-slate-300 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
          <div>© 2026 EcomCatalog AI · All rights reserved.</div>
          <div className="font-mono">v2.0 · Made in India</div>
        </div>
      </div>
    </footer>
  );
}
